import {
  Control,
  Controller,
  FieldArrayWithId,
  UseFormSetValue,
} from 'react-hook-form';
import { Doc, GPanelPermissionsModel } from '../../../types';
import UserAvatar from '../../shared/user-avatar';

import Stack from '@mui/material/Stack';
import ListItem from '@mui/material/ListItem';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Clear from '@mui/icons-material/Clear';

interface DriveFilePermissionListItemProps {
  isSharedDriveFile: boolean;
  watchedAcl: GPanelPermissionsModel;
  acl: FieldArrayWithId<
    {
      aclResources: GPanelPermissionsModel[];
    },
    'aclResources',
    'id'
  >;
  index: number;
  control: Control<{
    aclResources: GPanelPermissionsModel[];
  }>;
  setValue: UseFormSetValue<{
    aclResources: GPanelPermissionsModel[];
  }>;
  filteredData: GPanelPermissionsModel[];
  remove: (index?: number | number[]) => void;
  watchedAcls: GPanelPermissionsModel[];
  selectedDoc: Doc;
}

export default function DriveFilePermissionListItem({
  isSharedDriveFile,
  watchedAcl,
  acl,
  index,
  control,
  setValue,
  filteredData,
  remove,
  watchedAcls,
  selectedDoc,
}: DriveFilePermissionListItemProps) {
  // if it's a shared drive and it's a file:
  // Viewer, Commenter, Editor is available
  // Can't downgrade permission, so if Editor, can't change to anything else

  // if it's a shared drive and it's a folder:
  // Viewer, Commenter, Contributor, Content Manager, Manager is available
  //   Manager is not selectable
  //   You can downgrade permissions if they weren't inherited

  const isOwner = watchedAcl.role === 'owner';

  const isFolder =
    selectedDoc.mimeType === 'application/vnd.google-apps.folder';

  const permissionIsInherited =
    watchedAcl.permissionDetails.length > 0 &&
    watchedAcl.permissionDetails[0].inherited;

  const permissionCantBeDowngraded =
    isSharedDriveFile &&
    !isFolder &&
    (watchedAcl.role === 'organizer' || watchedAcl.role === 'fileOrganizer');

  const permissionCantBeDowngradedFromFolder =
    isSharedDriveFile && isFolder && watchedAcl.role === 'organizer';

  const shouldntBeAbleToChangeRole =
    watchedAcl.role === 'owner' ||
    permissionCantBeDowngraded ||
    permissionCantBeDowngradedFromFolder;

  return (
    <ListItem
      dense
      secondaryAction={
        <Stack flexDirection="row" gap={1} alignItems="center">
          <Controller
            name={`aclResources.${index}.role`}
            control={control}
            rules={{
              required: 'Role is required.',
            }}
            render={({ field }) => (
              <>
                <Tooltip
                  arrow
                  title={
                    permissionCantBeDowngraded || permissionIsInherited
                      ? `Can't downgrade member permissions`
                      : ''
                  }
                >
                  <TextField
                    {...field}
                    value={
                      (field.value === 'organizer' && !isFolder) ||
                      (field.value === 'fileOrganizer' && !isFolder)
                        ? 'writer'
                        : field.value
                    }
                    onChange={(e) => {
                      field.onChange(e);

                      // handle changeType
                      if (watchedAcl.changeType !== 'add') {
                        if (e.target.value === filteredData?.[index].role) {
                          setValue(`aclResources.${index}.changeType`, null, {
                            shouldDirty: true,
                          });
                        } else {
                          setValue(
                            `aclResources.${index}.changeType`,
                            'change',
                            { shouldDirty: true }
                          );
                        }
                      }

                      // handle transfer ownership
                      if (e.target.value === 'owner') {
                        watchedAcls.forEach((acl, i) => {
                          if (i !== index) {
                            if (acl.role === 'owner') {
                              setValue(`aclResources.${i}.role`, 'writer', {
                                shouldDirty: true,
                              });
                            }
                          } else {
                            setValue(
                              `aclResources.${i}.transferOwnership`,
                              true,
                              { shouldDirty: true }
                            );
                          }
                        });
                      }
                    }}
                    inputProps={{
                      id: `role-${index}`,
                    }}
                    InputLabelProps={{
                      htmlFor: `role-${index}`,
                    }}
                    select
                    defaultValue={acl.role}
                    disabled={
                      shouldntBeAbleToChangeRole || permissionIsInherited
                    }
                    label="Role"
                    size="small"
                    sx={{ width: 140 }}
                  >
                    {/* AVAILABLE TO ALL */}
                    <MenuItem key="reader" value="reader">
                      Viewer
                    </MenuItem>
                    <MenuItem key="commenter" value="commenter">
                      Commenter
                    </MenuItem>
                    <MenuItem key="writer" value="writer">
                      {isFolder ? 'Contributor' : 'Editor'}
                    </MenuItem>

                    {/* AVAILABLE TO JUST DRIVE FILES AND FOLDERS */}
                    {!isSharedDriveFile && (
                      <MenuItem key="owner" value="owner">
                        Owner
                      </MenuItem>
                    )}

                    {/* AVAILABLE TO JUST SHARED DRIVE FOLDERS */}
                    {isSharedDriveFile && isFolder && (
                      <MenuItem key="fileOrganizer" value="fileOrganizer">
                        Content Manager
                      </MenuItem>
                    )}

                    {isSharedDriveFile && isFolder && (
                      <MenuItem key="organizer" value="organizer" disabled>
                        Manager
                      </MenuItem>
                    )}
                  </TextField>
                </Tooltip>
                {!isOwner ? (
                  <Tooltip
                    arrow
                    title={
                      permissionIsInherited
                        ? `Can't remove permission because it is inherited ${
                            watchedAcl.permissionDetails[0].inheritedFrom ==
                            null
                              ? ''
                              : watchedAcl.permissionDetails[0]
                                  .permissionType === 'member'
                              ? ` from shared drive: "${watchedAcl.permissionDetails[0].inheritedFrom}"`
                              : ` from folder: "${watchedAcl.permissionDetails[0].inheritedFrom}"`
                          }`
                        : 'Remove'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => remove(index)}
                        disabled={permissionIsInherited}
                      >
                        <Clear />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Box sx={{ width: 34, height: 34 }}></Box>
                )}
              </>
            )}
          />
        </Stack>
      }
    >
      <ListItemAvatar>
        <UserAvatar userEmail={acl.userEmail || ''} />
      </ListItemAvatar>
      <ListItemText
        primary={acl.displayName === '' ? acl.userEmail : acl.displayName}
        primaryTypographyProps={{
          maxWidth: 200,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
        secondary={acl.userEmail}
        secondaryTypographyProps={{
          maxWidth: 200,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          fontSize: '12px',
        }}
      />
    </ListItem>
  );
}
